/**
 * Resaltados sobre la vista previa (el HTML renderizado).
 *
 * Se marca leyendo, y leer es la vista previa: acá la selección es la nativa
 * del navegador — doble clic para palabra, arrastre entre párrafos, todo gratis.
 *
 * Las anclas se miden contra el TEXTO RENDERIZADO, no contra el markdown de
 * origen. Es lo que evita el problema espinoso de mapear offsets del `.md` a
 * posiciones del HTML: el rango que devuelve el navegador ya está en el mismo
 * sistema de coordenadas en el que después se pinta. Y el `.md` sigue sin
 * enterarse de que existimos.
 */

import {
  type Highlight,
  highlightClass,
  makeAnchor,
  resolveAnchor,
} from "../model/highlights.ts";

function textNodesOf(root: HTMLElement): Text[] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const out: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) out.push(n as Text);
  return out;
}

/**
 * Texto plano del render: la cadena contra la que se resuelven las anclas.
 *
 * Coincide por construcción con lo que devuelve `Range.toString()` y con la
 * concatenación de los nodos de texto que recorre `paintRange`, así que medir y
 * pintar usan siempre la misma regla.
 */
export function previewText(root: HTMLElement): string {
  return root.textContent ?? "";
}

/** Offsets de la selección dentro de `root`, o null si no hay una utilizable. */
export function selectionRange(root: HTMLElement): { from: number; to: number } | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;

  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;

  // Medir el texto que queda ANTES del inicio de la selección da su offset
  // global sin tener que recorrer nodos a mano.
  const before = document.createRange();
  before.selectNodeContents(root);
  before.setEnd(range.startContainer, range.startOffset);

  const from = before.toString().length;
  const to = from + range.toString().length;
  return to > from ? { from, to } : null;
}

/**
 * Envuelve `[from, to)` en `<mark>`.
 *
 * Un rango que cruza elementos (por ejemplo dos párrafos, o texto y negrita)
 * no se puede envolver con un solo nodo, así que se emite un `<mark>` por cada
 * tramo. Visualmente es una marca continua.
 */
export function paintRange(
  root: HTMLElement,
  from: number,
  to: number,
  className: string,
): void {
  if (to <= from) return;

  // Se toman los nodos ANTES de partir ninguno: los trozos nuevos que genera
  // `splitText` no están en esta lista y por eso no se reprocesan.
  let pos = 0;
  for (const node of textNodesOf(root)) {
    const start = pos;
    const len = node.data.length;
    pos += len;
    if (pos <= from) continue;
    if (start >= to) break;

    const a = Math.max(0, from - start);
    const b = Math.min(len, to - start);
    if (b <= a) continue;

    const piece = a > 0 ? node.splitText(a) : node;
    if (b - a < piece.data.length) piece.splitText(b - a);

    const mark = document.createElement("mark");
    mark.className = className;
    piece.parentNode?.insertBefore(mark, piece);
    mark.appendChild(piece);
  }
}

/**
 * Ubica y pinta todos los resaltados sobre un markdown ya renderizado.
 *
 * Lo comparten la vista previa y las tarjetas del canvas: las dos parten del
 * mismo HTML (`renderMarkdown`), así que una marca se ve igual en los dos
 * lados sin duplicar la lógica.
 *
 * `refreshAnchors` reescribe el ancla de los que se pudieron ubicar, para que
 * el camino rápido siga sirviendo. La vista previa lo quiere (es dueña de esos
 * datos); el snapshot del canvas no, porque trabaja sobre una copia.
 */
export function paintHighlights(
  root: HTMLElement,
  highlights: readonly Highlight[],
  refreshAnchors = false,
): void {
  if (highlights.length === 0) return;
  const text = previewText(root);
  for (const h of highlights) {
    if (h.anchor.kind !== "text") continue; // un ancla de PDF no va sobre HTML
    const at = resolveAnchor(text, h.anchor);
    if (!at) continue; // huérfano: se conserva en disco, no se puede pintar
    if (refreshAnchors) h.anchor = makeAnchor(text, at.from, at.to);
    paintRange(root, at.from, at.to, highlightClass(h.color));
  }
}
